import { useAuth } from '@src/hooks/useAuth'
import { useForm } from 'react-hook-form'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Input,
  Label,
} from '@wove/web-ui'
import { useSetPersonalInfoMutation } from '@wove/api'
import { useEffect, useState } from 'react'
import { Briefcase, Pencil, Save } from 'lucide-react'

interface FormValues {
  firstName: string
  lastName: string
  jobTitle: string
}

const AccountSettings = () => {
  const { user, setUser } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      jobTitle: user?.jobTitle || '',
    },
  })

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        jobTitle: user.jobTitle || '',
      })
    }
  }, [user, reset])

  const [{ fetching: isSaving }, setPersonalInfo] = useSetPersonalInfoMutation()

  const onSubmit = async (data: FormValues) => {
    const response = await setPersonalInfo({
      profileInfo: {
        firstName: data.firstName,
        lastName: data.lastName,
        jobTitle: data.jobTitle,
      },
    })

    if (response.data) {
      setUser(response.data.setProfileInfo.user)
    }
  }

  if (!user) {
    return null
  }

  return (
    <section id="profile" className="scroll-mt-20">
      <form onSubmit={handleSubmit(onSubmit)} className="gap-3 py-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Profile</CardTitle>
              <CardDescription>Manage your personal information and preferences</CardDescription>
            </div>
            <Briefcase className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex flex-col items-center space-y-3">
                <Avatar className="h-24 w-24">
                  <AvatarImage
                    src="/placeholder-user.jpg"
                    alt={user.firstName + ' ' + (user.lastName || '')}
                  />
                  <AvatarFallback className="text-2xl">
                    {user.firstName[0] + (user.lastName?.[0] || '')}
                  </AvatarFallback>
                </Avatar>
                <Button variant="outline" size="sm" className="w-full">
                  Change Avatar
                </Button>
              </div>
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      error={errors.firstName?.message}
                      {...register('firstName', { required: 'First name is required' })}
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Last Name</Label>
                    <Input
                      id="lastName"
                      error={errors.lastName?.message}
                      {...register('lastName', { required: 'Last name is required' })}
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={user.email || ''}
                      readOnly
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Input
                      error={errors.jobTitle?.message}
                      {...register('jobTitle')}
                      disabled={!isEditing}
                    />
                  </div>
                  {/* <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={profile.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    disabled={!isEditing}
                  />
                </div> */}
                </div>
                {/* <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={profile.bio}
                  onChange={(e) => handleChange('bio', e.target.value)}
                  disabled={!isEditing}
                  className="min-h-[100px]"
                />
              </div> */}
              </div>
            </div>

            {/* <Separator />

          <div className="space-y-4">
            <h3 className="text-lg font-medium">Preferences</h3>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="public-profile">Public Profile</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow other team members to view your profile
                  </p>
                </div>
                <Switch
                  id="public-profile"
                  checked={profile.publicProfile}
                  onCheckedChange={(checked) => handleChange('publicProfile', checked)}
                  disabled={!isEditing}
                />
              </div>
            </div> */}
          </CardContent>
          <CardFooter className="flex justify-between">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving || !isDirty}>
                  {isSaving ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={() => setIsEditing(true)} className="ml-auto">
                <Pencil className="mr-2 h-4 w-4" />
                Edit Profile
              </Button>
            )}
          </CardFooter>
        </Card>
      </form>
    </section>
  )
}

export default AccountSettings
